import { createServerFn } from "@tanstack/react-start";
import { buildMonths, changeOver, mergeDaily, parseFredObservations, type RealYieldDay, type RealYieldMonth } from "@/lib/real-yield";

// US 10Y real yield (TIPS) and nominal 10Y from the official FRED API.
// Public data, hourly cache, last good response retained on source failure.

export type RealYieldDTO = {
  days: RealYieldDay[]; // ascending, up to 5 years of daily rows
  months: RealYieldMonth[]; // ascending monthly averages with gold returns
  latest: { date: string; real: number; nominal: number | null; weeklyChange: number | null; monthlyChange: number | null } | null;
  error?: string;
  stale?: boolean;
};

const FRED_URL = "https://api.stlouisfed.org/fred/series/observations";
const REAL_10Y = "DFII10"; // 10-Year TIPS constant maturity, daily, percent
const NOMINAL_10Y = "DGS10"; // 10-Year Treasury constant maturity, daily, percent
const TTL_MS = 60 * 60 * 1000;
let cache: { at: number; data: RealYieldDTO } | null = null;

async function fetchSeries(seriesId: string, start: string) {
  const apiKey = process.env.FRED_API_KEY;
  if (!apiKey) throw new Error("FRED_API_KEY missing");
  const url = new URL(FRED_URL);
  url.searchParams.set("series_id", seriesId);
  url.searchParams.set("api_key", apiKey);
  url.searchParams.set("file_type", "json");
  url.searchParams.set("observation_start", start);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`FRED HTTP ${res.status}`);
  const json = (await res.json()) as { observations?: { date: string; value: string }[] };
  return parseFredObservations(json.observations);
}

const pct = (a: number, b: number) => Math.round(((a - b) / b) * 1000) / 10;

export const getRealYield = createServerFn({ method: "GET" }).handler(async (): Promise<RealYieldDTO> => {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.data;
  try {
    const start = new Date(Date.now() - 5 * 365.25 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const [real, nominal] = await Promise.all([fetchSeries(REAL_10Y, start), fetchSeries(NOMINAL_10Y, start)]);
    if (real.length < 30) throw new Error("Insufficient real yield history");
    const days = mergeDaily(real, nominal);

    const gold = new Map<string, number>();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data } = await supabaseAdmin.from("gold_monthly_history").select("year,month,open,close").limit(1000);
      for (const r of data ?? []) {
        if (r.open > 0) gold.set(`${r.year}-${String(r.month).padStart(2, "0")}`, pct(r.close, r.open));
      }
    } catch (e) {
      console.error("[real-yield] gold history unavailable", e);
    }

    const months = buildMonths(days, gold);
    const last = days.filter((d) => d.real != null).at(-1)!;
    const data: RealYieldDTO = {
      days,
      months,
      latest: {
        date: last.date,
        real: last.real!,
        nominal: last.nominal,
        weeklyChange: changeOver(days, 5),
        monthlyChange: changeOver(days, 21),
      },
    };
    cache = { at: Date.now(), data };
    const { saveSnapshot } = await import("@/lib/macro-snapshot.server");
    await saveSnapshot("real-yield", data);
    return data;
  } catch (err) {
    console.error("[real-yield] fetch failed", err);
    if (cache) return { ...cache.data, stale: true };
    const { loadSnapshot } = await import("@/lib/macro-snapshot.server");
    const snap = await loadSnapshot<RealYieldDTO>("real-yield");
    if (snap?.days?.length) {
      cache = { at: Date.now() - TTL_MS + 10 * 60 * 1000, data: snap };
      return { ...snap, stale: true };
    }
    return { days: [], months: [], latest: null, error: "Real yield data is temporarily unavailable." };
  }
});
